import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { TimeKeepingStatusChipSelect } from '@/components/TimeKeepingStatusChipSelect';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchClip } from '@/features/clips/api/patch-clips';
import { TimeKeepingReviewStateEnum } from '@/features/clips/types';
import { useTimeKeepingStore } from '@/stores/time-keeping';
import { PresenceTypeEnum } from '@/types/presences';

import { BroadcastScheduleProgramBodySegmentToolkitTimeKeepingDashboard } from './components/BroadcastScheduleProgramBodySegmentToolkitTimeKeepingDashboard';
import { BroadcastScheduleProgramBodySegmentToolkitTimeKeepingToolbar } from './components/BroadcastScheduleProgramBodySegmentToolkitTimeKeepingToolbar';
import { UNDEFINED_SUPPORT_ID } from './constants';
import { DEFAULT_ITEM } from './constants';
import { TimeKeepingContext, TimeKeepingContextValue } from './context/TimeKeepingContext';
import { useTimeKeepingFilters } from './hooks/use-time-keeping-filters';
import { useTimeKeepingLayout } from './hooks/use-time-keeping-layout';
import { useTimeKeepingRecords } from './hooks/use-time-keeping-records';
import { useTimeKeepingSession } from './hooks/use-time-keeping-session';
import { ItemLayoutType, TimeKeepingDashboardHandle } from './types';
import { buildRecordsFromPeople } from './utils/build-records-from-people';
import { buildSessionItems } from './utils/build-session-items';
import { buildSupportsFromLogos } from './utils/build-supports-from-logos';
import { buildCompositeKey } from './utils/composite-key';
import { findFreeSpot } from './utils/find-free-spot';
import { patchClipCache } from './utils/patch-clip-cache';
import { reflowItems } from './utils/reflow-items';

export default function BroadcastScheduleProgramBodySegmentToolkitTimeKeeping() {
  const { t } = useTranslation();
  const { activeClip, setActiveClip, setClipItems, setCutData } = useBroadcastScheduleProgram();
  const { mutateAsync: patchClip } = usePatchClip();
  const setRecordsCount = useTimeKeepingStore(state => state.setRecordsCount);
  const dashboardRef = useRef<TimeKeepingDashboardHandle>(null);
  const lastInitializedClipIdRef = useRef<string | undefined>(undefined);
  // true when the last init ran against a clip with no logos/peoples (stale cache).
  // Allows one re-initialization when fresh data arrives with actual records.
  const initializedWithEmptyDataRef = useRef(false);

  const layout = useTimeKeepingLayout();
  const filters = useTimeKeepingFilters();

  const session = useTimeKeepingSession({
    items: layout.items,
    selectedPeople: filters.selectedPeople,
    dispatch: layout.dispatch,
    setSelectedLogos: filters.setSelectedLogos,
    setSelectedPeople: filters.setSelectedPeople,
    setSessionSupports: filters.setSessionSupports,
    dashboardRef,
    lastInitializedClipIdRef,
    activeClip,
  });

  const records = useTimeKeepingRecords({ items: layout.items, dispatch: layout.dispatch });

  // Initialize layout from clip data, or refresh session-scoped records when the clip changes
  useEffect(() => {
    if (!activeClip) {
      if (!session.activeSessionId) {
        lastInitializedClipIdRef.current = undefined;
        initializedWithEmptyDataRef.current = false;
        layout.dispatch({ type: 'RESET' });
        filters.setSelectedLogos([]);
        filters.setSelectedPeople([]);
        filters.setSessionSupports([]);
      }
      return;
    }

    if (activeClip.id === lastInitializedClipIdRef.current) {
      // Allow one re-initialization when the previous init used stale/empty data and fresh
      // records have now arrived (stale-while-revalidate background refetch scenario).
      if (!initializedWithEmptyDataRef.current) return;
      const hasIncomingData =
        (activeClip.clip_logos ?? []).length > 0 || (activeClip.clip_peoples ?? []).length > 0;
      if (!hasIncomingData) return;
      // Fall through to re-initialize with the fresh data.
    }

    if (session.activeSessionId) {
      const activeSession = session.sessions.find(s => s.id === session.activeSessionId);
      if (!activeSession) return;
      lastInitializedClipIdRef.current = activeClip.id;
      initializedWithEmptyDataRef.current = false;
      layout.dispatch({
        type: 'RESTORE',
        items: buildSessionItems({
          session: activeSession,
          clipLogos: activeClip.clip_logos ?? [],
          clipPeoples: activeClip.clip_peoples ?? [],
        }),
      });
      return;
    }

    lastInitializedClipIdRef.current = activeClip.id;

    const clipLogos = activeClip.clip_logos ?? [];
    const clipPeoples = activeClip.clip_peoples ?? [];

    initializedWithEmptyDataRef.current = clipLogos.length === 0 && clipPeoples.length === 0;

    const logoGroups = new Map<string, typeof clipLogos>();
    for (const entry of clipLogos) {
      if (!logoGroups.has(entry.logo_id)) logoGroups.set(entry.logo_id, []);
      logoGroups.get(entry.logo_id)!.push(entry);
    }

    const peopleGroups = new Map<string, typeof clipPeoples>();
    for (const entry of clipPeoples) {
      if (!peopleGroups.has(entry.people_id)) peopleGroups.set(entry.people_id, []);
      peopleGroups.get(entry.people_id)!.push(entry);
    }

    const newItems: ItemLayoutType[] = [];
    const supportIds = new Set<string>();
    let placedItems: ItemLayoutType[] = [];

    for (const [logoId, entries] of logoGroups) {
      const supports = buildSupportsFromLogos(entries);
      supports.forEach(s => {
        if (s.presenceId !== UNDEFINED_SUPPORT_ID) supportIds.add(s.presenceId);
      });
      const { x, y } = findFreeSpot(
        placedItems,
        DEFAULT_ITEM.w,
        DEFAULT_ITEM.h,
        layout.colsRef.current
      );
      const item: ItemLayoutType = {
        presence_type: PresenceTypeEnum.LOGOS,
        presence_id: logoId,
        supports,
        x,
        y,
        ...DEFAULT_ITEM,
      };
      placedItems = [...placedItems, item];
      newItems.push(item);
    }

    for (const [peopleId, entries] of peopleGroups) {
      const recItems = buildRecordsFromPeople(entries);
      const { x, y } = findFreeSpot(
        placedItems,
        DEFAULT_ITEM.w,
        DEFAULT_ITEM.h,
        layout.colsRef.current
      );
      const item: ItemLayoutType = {
        presence_type: PresenceTypeEnum.PEOPLE,
        presence_id: peopleId,
        supports: [],
        records: recItems,
        x,
        y,
        ...DEFAULT_ITEM,
      };
      placedItems = [...placedItems, item];
      newItems.push(item);
    }

    layout.dispatch({ type: 'RESTORE', items: newItems });
    filters.setSelectedLogos(Array.from(logoGroups.keys()));
    filters.setSelectedPeople(Array.from(peopleGroups.keys()));
    filters.setSessionSupports(Array.from(supportIds));
  }, [activeClip, session.activeSessionId, session.sessions]);

  // Sync total records count to Zustand store
  useEffect(() => {
    const total = layout.items.reduce((acc, item) => {
      if (item.presence_type === PresenceTypeEnum.PEOPLE) {
        return acc + (item.records?.length ?? 0);
      }
      return acc + item.supports.reduce((s, support) => s + support.records.length, 0);
    }, 0);
    setRecordsCount(total);
  }, [layout.items, setRecordsCount]);

  // ─── Orchestrated handlers ────────────────────────────────────────────────

  const handleLogosChange = useCallback(
    (newLogos: string[]) => {
      newLogos
        .filter(id => !filters.selectedLogos.includes(id))
        .forEach(id =>
          layout.dispatch({
            type: 'ADD_ITEM',
            presenceType: PresenceTypeEnum.LOGOS,
            presenceId: id,
            sessionSupports: filters.sessionSupports,
            cols: layout.colsRef.current,
          })
        );
      filters.selectedLogos
        .filter(id => !newLogos.includes(id))
        .forEach(id =>
          layout.dispatch({
            type: 'REMOVE_ITEM',
            compositeId: buildCompositeKey(PresenceTypeEnum.LOGOS, id),
          })
        );
      filters.setSelectedLogos(newLogos);
    },
    [
      filters.selectedLogos,
      filters.sessionSupports,
      layout.dispatch,
      layout.colsRef,
      filters.setSelectedLogos,
    ]
  );

  const handlePeopleChange = useCallback(
    (newPeople: string[]) => {
      newPeople
        .filter(id => !filters.selectedPeople.includes(id))
        .forEach(id =>
          layout.dispatch({
            type: 'ADD_ITEM',
            presenceType: PresenceTypeEnum.PEOPLE,
            presenceId: id,
            sessionSupports: [],
            cols: layout.colsRef.current,
          })
        );
      filters.selectedPeople
        .filter(id => !newPeople.includes(id))
        .forEach(id =>
          layout.dispatch({
            type: 'REMOVE_ITEM',
            compositeId: buildCompositeKey(PresenceTypeEnum.PEOPLE, id),
          })
        );
      filters.setSelectedPeople(newPeople);
    },
    [filters.selectedPeople, layout.dispatch, layout.colsRef, filters.setSelectedPeople]
  );

  const handleSupportsChange = useCallback(
    (newSupports: string[]) => {
      const prev = filters.sessionSupports;
      const added = newSupports.filter(id => !prev.includes(id));
      const removed = prev.filter(id => !newSupports.includes(id));
      layout.dispatch({ type: 'UPDATE_SESSION_SUPPORTS', added, removed });
      filters.setSessionSupports(newSupports);
    },
    [filters.sessionSupports, layout.dispatch, filters.setSessionSupports]
  );

  const handleAutoSort = useCallback(() => {
    const cols = dashboardRef.current?.getCols() ?? layout.colsRef.current;
    const currentLayout = dashboardRef.current?.getLayout() ?? [];
    const posMap = new Map(currentLayout.map(({ i, x, y }) => [i, { x, y }]));
    const itemsWithLivePos = layout.items.map(item => {
      const compositeId = buildCompositeKey(item.presence_type, item.presence_id);
      const pos = posMap.get(compositeId);
      return pos ? { ...item, x: pos.x, y: pos.y } : item;
    });
    layout.dispatch({ type: 'RESTORE', items: reflowItems(itemsWithLivePos, cols) });
  }, [layout.items, layout.dispatch, layout.colsRef]);

  const handleRemoveItem = useCallback(
    (compositeId: string) => {
      const idx = compositeId.indexOf(':');
      const presenceType = compositeId.slice(0, idx) as PresenceTypeEnum;
      const presenceId = compositeId.slice(idx + 1);
      layout.dispatch({ type: 'REMOVE_ITEM', compositeId });
      if (presenceType === PresenceTypeEnum.LOGOS) {
        filters.setSelectedLogos(filters.selectedLogos.filter(id => id !== presenceId));
      } else if (presenceType === PresenceTypeEnum.PEOPLE) {
        filters.setSelectedPeople(filters.selectedPeople.filter(id => id !== presenceId));
      }
    },
    [
      layout.dispatch,
      filters.selectedLogos,
      filters.selectedPeople,
      filters.setSelectedLogos,
      filters.setSelectedPeople,
    ]
  );

  const ctx: TimeKeepingContextValue = {
    items: layout.items,
    sessionSupports: filters.sessionSupports,
    selectedLogos: filters.selectedLogos,
    selectedPeople: filters.selectedPeople,
    sessions: session.sessions,
    activeSessionId: session.activeSessionId,
    onRemoveItem: handleRemoveItem,
    onColsChange: layout.handleColsChange,
    onItemSupportsChange: (compositeId, selectedIds) =>
      layout.dispatch({ type: 'SET_ITEM_SUPPORTS', compositeId, selectedIds }),
    onAddRecord: records.addRecord,
    onUpdateRecord: records.updateRecord,
    onRemoveRecord: records.removeRecord,
    onResetRecords: records.resetRecords,
    buildNewRecord: records.buildNewRecord,
    onLogosChange: handleLogosChange,
    onPeopleChange: handlePeopleChange,
    onSupportsChange: handleSupportsChange,
    onSaveSession: session.saveSession,
    onLoadSession: session.loadSession,
    onClearSession: session.clearSession,
    onDeleteSession: session.deleteActiveSession,
    onAutoSort: handleAutoSort,
  };

  const handleTimeKeepingStateChange = async (next: TimeKeepingReviewStateEnum) => {
    if (!activeClip?.id) return;
    const updated = await patchClip({ id: activeClip.id, time_keeping_state: next });
    patchClipCache(activeClip.id, clip => ({
      ...clip,
      time_keeping_state: updated.time_keeping_state,
    }));
    setActiveClip(prev =>
      prev ? { ...prev, time_keeping_state: updated.time_keeping_state } : prev
    );
    setCutData(prev => (prev ? { ...prev, time_keeping_state: updated.time_keeping_state } : prev));
    setClipItems(prev =>
      prev.map(item =>
        item.id === updated.id ? { ...item, time_keeping_state: updated.time_keeping_state } : item
      )
    );
  };

  const getTimeKeepingStatus = () => {
    if (!activeClip) return null;
    return (
      <TimeKeepingStatusChipSelect
        value={activeClip.time_keeping_state ?? TimeKeepingReviewStateEnum.WITHOUT_REVIEW}
        onChange={handleTimeKeepingStateChange}
      />
    );
  };

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2, min-h-full overflow-x-hidden'),
        rootDiv: 'min-h-full',
        container: 'py-size-4 min-h-full',
      }}
    >
      <TimeKeepingContext.Provider value={ctx}>
        <div className="flex w-full flex-col gap-2">
          <div className="flex flex-col gap-3">
            <GeneralHeader name={t('Toma de tiempos')} />
            <div className="flex w-full justify-end">{getTimeKeepingStatus()}</div>
          </div>
          <BroadcastScheduleProgramBodySegmentToolkitTimeKeepingToolbar />
          <BroadcastScheduleProgramBodySegmentToolkitTimeKeepingDashboard ref={dashboardRef} />
        </div>
      </TimeKeepingContext.Provider>
    </Card>
  );
}
